/**
 * Pure helpers для BotsWidgetContext.
 *
 * Тут живёт вся логика «как определить какая модель должна быть открыта
 * в виджете на /bots/*» — отдельно от React, чтобы:
 *   1. Легко тестировать (без рендера компонентов)
 *   2. Переиспользовать в других местах при необходимости
 *   3. Не зашумлять сам context-провайдер
 *
 * Priority chain (какую МОДЕЛЬ показать для текущего pathname):
 *   1. Pending generation — применяется в самом context'е, не здесь
 *      (требует GlobalGeneration)
 *   2. Last-used model для ТЕКУЩЕЙ category (saved в localStorage map)
 *   3. Default model для category (Nano Banana Pro / Kling / Suno)
 *   4. Final fallback: первая модель этой category из allModels
 *
 * Коллапс (свёрнут / раскрыт) — ОТДЕЛЬНОЕ глобальное состояние, тут не
 * решается. См. BOTS_WIDGET_COLLAPSED_KEY + load/saveCollapsedState ниже.
 */
import type { Model, ModelCategory } from '../../../data/models'
import {
	BOTS_WIDGET_COLLAPSED_KEY,
	WIDGET_LAST_MODEL_MAP_KEY,
} from '../../generate/utils/constants'
import { isTtsModelId, isTtsRoute, modelsForAudioRoute } from '../../generate/utils/ttsModels'

// ── Типы ──────────────────────────────────────────────────────

/** Тип для нашей per-category map в localStorage. 'tts' — отдельная ячейка
 *  моделей озвучки: они тоже 'audio', но выбор на «Озвучке» не должен затирать
 *  последнюю музыкальную модель (и наоборот). */
export type WidgetLastModelMap = Partial<Record<ModelCategory | 'tts', string>>

/** Результат резолвинга — какую модель показать */
export type WidgetResolution =
	| { action: 'open'; model: Model; reason: 'last-used' | 'default' | 'fallback' }
	| { action: 'skip'; reason: 'no-models' | 'unknown-category' }

// ── Route → category mapping ─────────────────────────────────

/**
 * Определяет категорию по pathname. Возвращает null для не-/bots/* страниц.
 *
 * /bots/images         → 'image'
 * /bots/images/styles  → 'image'
 * /bots/trends         → 'image'
 * /bots/video          → 'video'
 * /bots/audio          → 'audio'
 * /bots/slides         → 'presentation'  (legacy URL, редиректит на /bots/tools/slides)
 * /bots/tools/slides   → 'presentation'  (презентации-инструмент)
 * /other               → null
 */
export function getCategoryFromPath(pathname: string): ModelCategory | null {
	if (pathname.startsWith('/bots/images')) return 'image'
	// Тренды — отдельная страница на движке инструментов, фото-центрична:
	// виджет idle открывает image-модель (как /bots/images); клик по тренду
	// грузит его модель/пайплайн через applyPopularItem.
	if (pathname.startsWith('/bots/trends')) return 'image'
	if (pathname.startsWith('/bots/video')) return 'video'
	if (pathname.startsWith('/bots/audio')) return 'audio'
	if (pathname.startsWith('/bots/slides')) return 'presentation'
	// Презентации-инструмент: /bots/tools/slides тоже включает виджет в режиме
	// презентаций. Остальные /bots/tools/* виджета не дают (return null ниже).
	if (pathname.startsWith('/bots/tools/slides')) return 'presentation'
	return null
}

// ── Default model resolver ────────────────────────────────────

/**
 * Список preferred model id'ов по category (в порядке приоритета).
 * ID могут быть как frontend-short (`nanobanana-pro`) так и
 * backend-full (`google/nano-banana-pro`) — ищем по обоим полям.
 *
 * Если ни один не найден — fallback на первую модель этой category
 * из `allModels` (что-то всегда лучше чем null).
 */
const DEFAULT_MODEL_IDS: Record<ModelCategory, string[]> = {
	image: [
		'nanobanana-pro',
		'google/nano-banana-pro',
		'nano-banana-pro',
	],
	video: [
		'kling-2.6-i2v',
		'kling-2.6/image-to-video',
		'kling-v3-video',
		'kwaivgi/kling-v3-video',
	],
	audio: [
		'suno-v6',
		'suno-v5_5',
		'suno-v5',
		'suno-v4_5all',
		'suno-v4_5',
	],
	// Presentation: Gamma по умолчанию (режим «Создать с нуля»). Umnik-Slides
	// подключается автоматически при переключении в режим «Редактировать».
	// Обе модели подмешиваются в allModels через Phase A fallback.
	presentation: [
		'gamma-presentation',
		'umnik-slides',
	],
	// text модели не открываются в /bots/* виджете
	text: [],
}

/**
 * Ищет preferred default модель для category.
 * Возвращает null только если в allModels нет ни одной модели нужной category.
 */
export function findDefaultModel(
	category: ModelCategory,
	allModels: Model[],
): { model: Model; reason: 'default' | 'fallback' } | null {
	const preferredIds = DEFAULT_MODEL_IDS[category] || []

	// Пробуем preferred список
	for (const id of preferredIds) {
		const found = allModels.find((m) => m.id === id || m.backendModel === id)
		if (found) return { model: found, reason: 'default' }
	}

	// Fallback: первая модель этой category
	const fallback = allModels.find((m) => m.category === category)
	if (fallback) return { model: fallback, reason: 'fallback' }

	return null
}

// ── Last-used model persistence (per-category) ────────────────

/**
 * Читает мапу last-used моделей из localStorage.
 * Безопасно на невалидном JSON (возвращает {}).
 */
export function loadLastModelMap(): WidgetLastModelMap {
	try {
		const raw = localStorage.getItem(WIDGET_LAST_MODEL_MAP_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (typeof parsed === 'object' && parsed !== null) return parsed
		return {}
	} catch {
		return {}
	}
}

/**
 * Сохраняет last-used модель для category.
 * Если modelId = null — удаляет запись для этой category.
 */
export function saveLastModel(category: ModelCategory, modelId: string | null): void {
	try {
		const map = loadLastModelMap()
		if (modelId) {
			map[isTtsModelId(modelId) ? 'tts' : category] = modelId
		} else {
			delete map[category]
		}
		localStorage.setItem(WIDGET_LAST_MODEL_MAP_KEY, JSON.stringify(map))
	} catch {
		// QuotaExceeded или что-то ещё — игнорим, не критично
	}
}

/**
 * Ищет last-used модель для category в allModels.
 * Возвращает null если:
 *   - Нет записи в map
 *   - Записанная модель больше не существует (бэк выключил)
 *   - Записанная модель в другой category (несогласованность)
 */
export function resolveLastUsedModel(
	category: ModelCategory,
	allModels: Model[],
	slot: ModelCategory | 'tts' = category,
): Model | null {
	const map = loadLastModelMap()
	const savedId = map[slot]
	if (!savedId) return null
	const found = allModels.find((m) => m.id === savedId)
	if (!found) return null
	// Защита от несогласованности: если записали id из image в image, а
	// теперь модель в API числится как video — не открываем
	if (found.category !== category) return null
	return found
}

// ── Collapsed state persistence (глобально, не per-category) ─

/**
 * Читает глобальный флаг «виджет свёрнут». Один ключ на всё приложение.
 * По умолчанию — false (раскрыт).
 */
export function loadCollapsedState(): boolean {
	try {
		return localStorage.getItem(BOTS_WIDGET_COLLAPSED_KEY) === 'true'
	} catch {
		return false
	}
}

/**
 * Записывает глобальный флаг «виджет свёрнут».
 * На false — удаляем ключ, чтобы не замусоривать storage.
 */
export function saveCollapsedState(collapsed: boolean): void {
	try {
		if (collapsed) {
			localStorage.setItem(BOTS_WIDGET_COLLAPSED_KEY, 'true')
		} else {
			localStorage.removeItem(BOTS_WIDGET_COLLAPSED_KEY)
		}
	} catch {
		// игнорим
	}
}

// ── Priority chain resolver ──────────────────────────────────

/**
 * Главная функция: определяет, какую модель показать в виджете
 * для текущего pathname + allModels. Учитывает last-used, default.
 *
 * НЕ решает: открыт/свёрнут виджет (это отдельный state в контексте).
 * НЕ проверяет pending generation — это делает context отдельно (выше priority).
 *
 * @param pathname текущий route pathname
 * @param allModels все доступные модели (из useProviders)
 */
export function resolveWidgetModel(
	pathname: string,
	allModels: Model[],
): WidgetResolution {
	const category = getCategoryFromPath(pathname)
	if (!category) return { action: 'skip', reason: 'unknown-category' }

	// Если категории text добавятся в /bots/* в будущем — тоже skip
	if (category === 'text') return { action: 'skip', reason: 'unknown-category' }

	// Аудио: на «Озвучке» (/bots/audio/tts) выбираем только из моделей озвучки
	// (ElevenLabs), на остальных аудио-страницах — только из музыкальных. Иначе на
	// reload «Озвучки» загрузилась бы last-used Suno и tts-режим не включился бы
	// (а на «Песнях» — модель озвучки, которая музыку не пишет).
	const pool = modelsForAudioRoute(allModels, pathname)

	// Priority: last-used → default → fallback
	const lastUsed = resolveLastUsedModel(category, pool, isTtsRoute(pathname) ? 'tts' : category)
	if (lastUsed) {
		return { action: 'open', model: lastUsed, reason: 'last-used' }
	}

	const defaultResult = findDefaultModel(category, pool)
	if (defaultResult) {
		return { action: 'open', model: defaultResult.model, reason: defaultResult.reason }
	}

	return { action: 'skip', reason: 'no-models' }
}
